import { memo } from 'react';
import { css } from '@emotion/react';
import { useAccessibilityModeEnabled } from '@frontend/accessibility';
import { getUser, type PortalUser } from '@frontend/auth-helpers';
import { useNavSize } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';
import { Avatar, Dot, NakedButton } from '@frontend/design-system';
import { useApp } from '../../../../providers/app.provider';
import { useTopBarShallowStore, useTopBarStore } from '../../store/use-top-bar-store';
import { iconButtonStyles } from '../../styles/styles';
import { TopBarFocusIds } from '../../utils/focus-registry';

type ProfileButtonWrapper = {
  isSelected: boolean;
  isMobile: boolean;
  onClick: (e: any) => void;
  edge: boolean;
  dotVisible: boolean;
  user?: PortalUser;
};

export const ProfileButton = memo(({ isSelected, isMobile, onClick, user, edge, dotVisible }: ProfileButtonWrapper) => (
  <NakedButton
    css={[
      iconButtonStyles({ isSelected, isMobile }),
      css`
        position: relative;
      `,
    ]}
    onClick={onClick}
    id={TopBarFocusIds.profile}
    trackingId='top-bar-profile-tray-trigger'
  >
    <Avatar
      isUser
      name={user?.firstName + ' ' + user?.lastName}
      size='xs'
      disableClick
      css={css`
        > div {
          border: ${edge ? '1px solid black' : 'none'};
        }
      `}
    >
      {dotVisible && (
        <Dot
          color='critical'
          css={{
            width: theme.spacing(1.5),
            height: theme.spacing(1.5),
            top: theme.spacing(-0.75),
            right: theme.spacing(-0.5),
            border: `1.5px solid ${theme.colors.white}`,
          }}
        />
      )}
    </Avatar>
  </NakedButton>
));

ProfileButton.displayName = 'ProfileButton';

export const ProfileButtonWrapper = () => {
  const { currentChannel } = useApp();
  const navSize = useNavSize();
  const isMobile = navSize.isLte('mobile');
  const user = getUser();
  const { handleSelectableIconClick, nextVersion } = useTopBarShallowStore('handleSelectableIconClick', 'nextVersion');
  const isSelected = useTopBarStore((state) => state.selectedIcon === 'profile');
  const { isEnabled: accessibilityModeEnabled } = useAccessibilityModeEnabled('isEnabled');

  return (
    <li>
      <ProfileButton
        isSelected={isSelected}
        isMobile={isMobile}
        onClick={(e: any) => handleSelectableIconClick('profile', e)}
        user={user}
        edge={currentChannel?.slug === 'edge'}
        dotVisible={!!nextVersion || !!accessibilityModeEnabled}
      />
    </li>
  );
};
